import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  Search,
  ArrowLeft,
  FileVideo,
  Clock,
  UserCheck,
  AlertTriangle,
  Trophy,
} from "lucide-react";
import { BrandLogo } from "@/projects/components/brand-logo";
import { Button } from "@/projects/components/ui/button";
import { Input } from "@/projects/components/ui/input";
import { Label } from "@/projects/components/ui/label";
import { Card } from "@/projects/components/ui/card";
import { StatusBadge } from "@/projects/components/status-badge";
import { getSubmissions, type Submission } from "@/projects/lib/store";
import { toast } from "@/projects/lib/toast";

export const Route = createFileRoute("/status")({
  head: () => ({
    meta: [
      { title: "Check Submission Status — રાજનગર જ્ઞાનોત્સવ 2.0" },
      {
        name: "description",
        content:
          "Look up your Rajnagar Gyanotsav 2.0 submission status using your reference number.",
      },
    ],
  }),
  component: StatusPage,
});

function StatusPage() {
  const [ref, setRef] = useState("");
  const [result, setResult] = useState<Submission | null | "notfound">(null);
  const [loading, setLoading] = useState(false);

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ref.trim()) {
      toast.error("Please enter your reference number");
      return;
    }
    setLoading(true);
    await new Promise(r => setTimeout(r, 350));
    const found = getSubmissions().find(
      s => s.id.toLowerCase() === ref.trim().toLowerCase()
    );
    setResult(found ?? "notfound");
    setLoading(false);
  };

  const stepIcons = [
    { key: "Pending", label: "Received", icon: FileVideo },
    {
      key: "Assigned for Verification",
      label: "Under Review",
      icon: UserCheck,
    },
    { key: "Verified", label: "Verified", icon: Trophy },
  ];

  const currentStep = (s: Submission) => {
    if (s.status === "Pending") {
      return 0;
    }
    if (s.status === "Assigned for Verification") {
      return 1;
    }
    return 2;
  };

  return (
    <div className="bg-background min-h-screen">
      <header className="bg-background/80 sticky top-0 z-40 border-b backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-3 px-4">
          <Link to="/" className="flex min-w-0 items-center gap-2.5">
            <BrandLogo className="h-10 w-10" />
            <div className="min-w-0">
              <div className="text-guj truncate text-sm leading-tight font-bold sm:text-base">
                રાજનગર જ્ઞાનોત્સવ 2.0
              </div>
              <div className="text-muted-foreground text-[10px] font-medium tracking-widest uppercase">
                Check Status
              </div>
            </div>
          </Link>
          <div className="flex-1" />
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="mr-1 h-4 w-4" /> Back to home
            </Button>
          </Link>
        </div>
      </header>

      <section className="relative overflow-hidden py-12 lg:py-16">
        <div className="gradient-hero absolute inset-0 opacity-10" />
        <div className="motif-bg absolute inset-0 opacity-40" />
        <div className="relative mx-auto max-w-2xl px-4 text-center">
          <div className="border-royal/30 bg-royal-soft text-royal inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium">
            <Search className="h-3.5 w-3.5" /> Submission Tracker
          </div>
          <h1 className="mt-3 text-3xl font-bold tracking-tight lg:text-4xl">
            Track Your Submission
          </h1>
          <p className="text-muted-foreground mt-2">
            Enter the reference number you received after submitting your video.
          </p>
        </div>
      </section>

      <section className="pb-16">
        <div className="mx-auto max-w-2xl px-4">
          <Card className="shadow-card p-6">
            <form onSubmit={handleLookup} className="space-y-4">
              <div>
                <Label htmlFor="ref">Reference Number</Label>
                <Input
                  id="ref"
                  value={ref}
                  onChange={e => setRef(e.target.value)}
                  placeholder="e.g. RGU-2001"
                  className="font-mono"
                />
                <p className="text-muted-foreground mt-1 text-xs">
                  Format: RGU-XXXX (case insensitive)
                </p>
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  "Searching…"
                ) : (
                  <>
                    <Search className="mr-2 h-4 w-4" /> Check Status
                  </>
                )}
              </Button>
            </form>
          </Card>

          {result === "notfound" && (
            <Card className="border-destructive/30 bg-destructive/5 mt-6 p-6 text-center">
              <AlertTriangle className="text-destructive mx-auto h-10 w-10" />
              <div className="mt-3 font-semibold">
                Reference number not found
              </div>
              <p className="text-muted-foreground mt-1 text-sm">
                Please double-check the number in your submission receipt.
              </p>
            </Card>
          )}

          {result && result !== "notfound" && (
            <Card className="shadow-card mt-6 overflow-hidden">
              <div className="gradient-hero p-5 text-white">
                <div className="text-xs tracking-wider text-white/80 uppercase">
                  Reference Number
                </div>
                <div className="mt-1 font-mono text-2xl font-bold">
                  {result.id}
                </div>
                <div className="mt-2 text-sm text-white/90">
                  {result.videoTitle}
                </div>
              </div>
              <div className="p-6">
                <div className="mb-6 grid gap-2 sm:grid-cols-2">
                  <InfoRow
                    k="Student"
                    v={`${result.studentName} · ${result.studentId}`}
                  />
                  <InfoRow
                    k="Submitted"
                    v={new Date(result.submittedAt).toLocaleString()}
                  />
                  <InfoRow
                    k="File"
                    v={`${result.fileName} (${result.fileSizeMB} MB)`}
                  />
                  <InfoRow
                    k="Current Status"
                    v={<StatusBadge status={result.status} />}
                  />
                </div>

                <div className="bg-muted/30 rounded-lg border p-4">
                  <div className="text-muted-foreground mb-3 text-xs font-semibold tracking-wider uppercase">
                    Progress
                  </div>
                  <div className="flex items-center justify-between">
                    {stepIcons.map((s, i) => {
                      const step = currentStep(result);
                      const active = i <= step;
                      const Icon = s.icon;
                      return (
                        <div
                          key={s.key}
                          className="flex flex-1 flex-col items-center"
                        >
                          <div
                            className={`grid h-10 w-10 place-items-center rounded-full border-2 ${active ? "border-royal bg-royal text-white" : "border-muted-foreground/30 bg-background text-muted-foreground"}`}
                          >
                            <Icon className="h-4 w-4" />
                          </div>
                          <div
                            className={`mt-1.5 text-center text-[11px] font-medium ${active ? "text-foreground" : "text-muted-foreground"}`}
                          >
                            {s.label}
                          </div>
                          {i < stepIcons.length - 1 && (
                            <div
                              className={`absolute mt-5 h-0.5 w-[calc(33%-2.5rem)] translate-x-14 ${i < step ? "bg-royal" : "bg-muted-foreground/20"}`}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {result.teacherRemarks && (
                  <div className="bg-card mt-4 rounded-lg border p-4">
                    <div className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                      Verifier Remarks
                    </div>
                    <div className="mt-1 text-sm">{result.teacherRemarks}</div>
                    {result.verifiedAt && (
                      <div className="text-muted-foreground mt-1 text-xs">
                        Verified on{" "}
                        {new Date(result.verifiedAt).toLocaleString()}
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-5">
                  <div className="text-muted-foreground mb-2 text-xs font-semibold tracking-wider uppercase">
                    Activity Timeline
                  </div>
                  <ol className="border-muted space-y-2 border-l-2 pl-4">
                    {result.timeline.map(t => (
                      <li key={`${t.at}-${t.label}`} className="relative">
                        <div className="bg-royal absolute top-1.5 -left-[21px] h-2.5 w-2.5 rounded-full" />
                        <div className="text-sm">{t.label}</div>
                        <div className="text-muted-foreground text-xs">
                          {new Date(t.at).toLocaleString()}
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </Card>
          )}

          <Card className="border-royal/20 bg-royal-soft/30 mt-6 p-4">
            <div className="flex items-start gap-3">
              <Clock className="text-royal mt-0.5 h-5 w-5 shrink-0" />
              <div className="text-sm">
                <div className="font-medium">Haven&apos;t submitted yet?</div>
                <div className="text-muted-foreground">
                  <Link to="/" className="text-royal hover:underline">
                    Submit your video
                  </Link>{" "}
                  and receive a reference number instantly.
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
}

function InfoRow({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="bg-background rounded-md border p-2.5">
      <div className="text-muted-foreground text-[10px] font-semibold tracking-wider uppercase">
        {k}
      </div>
      <div className="mt-0.5 text-sm">{v}</div>
    </div>
  );
}
